import React, { useEffect, useState, useMemo } from "react";
import { useDealData } from "contexts/DealContext";
import Card from "components/card";
import { MdDownload } from "react-icons/md";
import { Button } from '@aws-amplify/ui-react';
import {
  createColumnHelper,
  useReactTable,
  getCoreRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  flexRender,
} from "@tanstack/react-table";
import { exportTableToCSV } from "utils/csvExport";
import ColumnSelector from "./ColumnSelector";

const columnHelper = createColumnHelper();

// Helper to get value from nested path
const getNestedValue = (obj, path) => {
  return path.split('.').reduce((acc, part) => acc && acc[part], obj);
};

function EnhancedTable({ selectedGroup, overrideData }) {
  const dealContext = useDealData();
  const { currentDeal, selectedPeriod, currentFacility, currentDealName } = overrideData || dealContext;
  const [tableData, setTableData] = useState([]);
  const [selectedColumns, setSelectedColumns] = useState([]);
  const [columnFilters, setColumnFilters] = useState([]);
  const [globalFilter, setGlobalFilter] = useState("");

  const availableColumns = useMemo(() => {
    if (!currentDeal?.facilities) return [];

    const columns = new Map();
    const facilities = Object.values(currentDeal.facilities)
      .filter(facility => facility.details?.Facilities !== 'Consolidated');

    // First collect a complete set of possible metrics from all facilities
    const allMetrics = {
      occupancy: new Set(),
      revenue: new Set(),
      expenses: new Set(),
    };

    facilities.forEach(facility => {
      const periodData = facility.periods[selectedPeriod]?.Categories || {};
      periodData.Occupancy?.forEach(metric => allMetrics.occupancy.add(metric.Type));
      periodData.Revenue?.forEach(metric => allMetrics.revenue.add(metric.Type));
      periodData.Expenses?.forEach(metric => allMetrics.expenses.add(metric.Type));
    });

    // Collect all unique detail fields across all facilities
    facilities.forEach(facility => {
      if (facility.details) {
        Object.keys(facility.details)
          .filter(key => key !== 'Facilities' && key.toLowerCase() !== 'coordinates')
          .forEach(key => {
            const columnId = `details.${key}`;
            if (!columns.has(columnId)) {
              columns.set(columnId, {
                id: columnId,
                label: key,
                category: 'Facility Details',
                accessor: `details.${key}`,
              });
            }
          });
      }
    });

    const medicaidRates = facilities.map(facility => {
      if (facility.hudDebt?.Categories) {
        const medicaidData = Object.entries(facility.hudDebt.Categories)
          .find(([key]) => key.startsWith("Med")) || [];

        if (medicaidData.length > 0) {
          const [_, data] = medicaidData;
          const rateItem = data.find(item =>
            item.Type?.includes("Med") ||
            item.Type?.includes("Total Rate") ||
            item.Type?.includes("Current")
          );
          return rateItem?.Amount || '';
        }
      }
      return '';
    }).filter(rate => rate !== '');

    // Only add Medicaid Rate column if at least one facility has a rate
    if (medicaidRates.length > 0) {
      const columnId = 'details.Medicaid Rate';
      columns.set(columnId, {
        id: columnId,
        label: "Medicaid Rate",
        category: 'Facility Details',
        accessor: 'details.Medicaid Rate'
      });
    }

    // Add HUD Debt columns if any facility has HUD Debt data
    const hasHudDebtFields = {
      'Original Loan': false,
      'Current Loan Balance': false,
      'Interest Rate': false,
      'Debt Service': false,
      'Term': false,
      'Endorsement Date': false
    };

    // Check if any facility has HUD debt fields
    facilities.forEach(facility => {
      if (facility.hudDebt?.Categories?.["HUD Debt"]) {
        const hudDebtData = facility.hudDebt.Categories["HUD Debt"];
        Object.keys(hasHudDebtFields).forEach(field => {
          // Check if the field exists in the HUD debt data
          const dataExists = hudDebtData.some(item =>
            item.Type === field &&
            (item.Amount !== undefined || item.Date !== undefined || item.Rate !== undefined)
          );
          if (dataExists) hasHudDebtFields[field] = true;
        });
      }
    });

    // Add HUD Debt columns for fields that exist in at least one facility
    Object.entries(hasHudDebtFields).forEach(([field, exists]) => {
      if (exists) {
        const columnId = `hudDebt.${field}`;
        let accessor;
        let formatter;

        // Determine the property and formatter based on the field type
        if (field === 'Interest Rate') {
          accessor = row => {
            const hudDebtData = row.hudDebt?.["HUD Debt"] || [];
            const item = hudDebtData.find(item => item.Type === field);
            return item?.Rate;
          };
          formatter = value => value ? `${(value * 100).toFixed(2)}%` : '';
        } else if (field === 'Endorsement Date') {
          accessor = row => {
            const hudDebtData = row.hudDebt?.["HUD Debt"] || [];
            const item = hudDebtData.find(item => item.Type === field);
            return item?.Date;
          };
          formatter = value => value || '';
        } else {
          accessor = row => {
            const hudDebtData = row.hudDebt?.["HUD Debt"] || [];
            const item = hudDebtData.find(item => item.Type === field);
            return item?.Amount;
          };
          formatter = value => {
            if (typeof value === 'number') {
              return `$${Math.round(value).toLocaleString()}`;
            }
            return value || '';
          };
        }

        columns.set(columnId, {
          id: columnId,
          label: field,
          category: 'HUD Debt',
          accessor: accessor,
          formatter: formatter
        });
      }
    });

    // Add period-based metrics by checking all facilities
    facilities.forEach(facility => {
      if (facility.periods && selectedPeriod) {
        const periodData = facility.periods[selectedPeriod]?.Categories || {};

        // Process Occupancy metrics
        if (periodData.Occupancy) {
          const processedTypes = new Set();
          periodData.Occupancy.forEach(metric => {
            // Avoid processing the same type multiple times
            if (processedTypes.has(metric.Type)) return;
            processedTypes.add(metric.Type);

            // Generate columns based on available data
            const columnTypes = [
              { suffix: 'Annual', prop: 'Annual' },
              { suffix: 'ADC', prop: 'PPD' },
              { suffix: 'Percentages', prop: 'Percentages' }
            ];

            columnTypes.forEach(({ suffix, prop }) => {
              // Only create column if the property exists and is not an empty string
              if (metric[prop] !== undefined && metric[prop] !== '') {
                const columnId = `occupancy.${metric.Type}.${suffix}`;
                if (!columns.has(columnId)) {
                  const label = suffix === 'Percentages' ? `${metric.Type} Percentage` : `${metric.Type} ${suffix}`;
                  columns.set(columnId, {
                    id: columnId,
                    label: label,
                    category: 'Occupancy',
                    accessor: (row) => {
                      const metricData = row.occupancy?.find(m => m.Type === metric.Type);
                      return metricData?.[prop] || '';
                    },
                  });
                }
              }
            });
          });
        }

        // Process Revenue metrics
        if (periodData.Revenue) {
          periodData.Revenue.forEach(metric => {

            // Generate columns based on available data
            const columnTypes = [
              { suffix: 'Annual', prop: 'Annual' },
              { suffix: 'PPD', prop: 'PPD' },
            ];

            columnTypes.forEach(({ suffix, prop }) => {
              // Only create column if the property exists and is not an empty string
              if (metric[prop] !== undefined && metric[prop] !== '') {
                const columnId = `revenue.${metric.Type}.${suffix}`;
                if (!columns.has(columnId)) {
                  const label = suffix === 'Percentages' ? `${metric.Type} Percentage` : `${metric.Type} ${suffix}`;
                  columns.set(columnId, {
                    id: columnId,
                    label: label,
                    category: 'Revenue',
                    accessor: (row) => {
                      const metricData = row.revenue?.find(m => m.Type === metric.Type);
                      return metricData?.[prop] || '';
                    },
                  });
                }
              }
            });
          });
        }

        // Process Expense metrics
        if (periodData.Expenses) {
          periodData.Expenses.forEach(metric => {
            // Skip EBITDAR check and unmapped expenses
            if (metric.Type.toLowerCase().includes('ebitdar check') ||
              metric.Type.toLowerCase().includes('unmapped expenses')) return;

            if (metric.Type.toLowerCase() === "adjusted ebitdar") {
              // Handle EBITDAR Annual amount
              const annualColumnId = `expenses.Adjusted EBITDAR.Annual`;
              if (!columns.has(annualColumnId)) {
                columns.set(annualColumnId, {
                  id: annualColumnId,
                  label: 'Adjusted EBITDAR',
                  category: 'Expenses',
                  accessor: (row) => {
                    const metricData = row.expenses?.find(m =>
                      m.Type.toLowerCase().includes('ebitdar') &&
                      m.Type.toLowerCase().includes('adjusted')
                    );
                    return metricData?.Annual || '';
                  },
                });
              }


              return;
            }

            // Generate columns based on available data
            const columnTypes = [
              { suffix: 'Annual', prop: 'Annual' },
              { suffix: 'PPD', prop: 'PPD' },
              { suffix: 'Percentages', prop: 'Percentages' }
            ];

            columnTypes.forEach(({ suffix, prop }) => {
              // Only create column if the property exists and is not an empty string
              if (metric[prop] !== undefined && metric[prop] !== '') {
                const columnId = `expenses.${metric.Type}.${suffix.toLowerCase()}`;
                if (!columns.has(columnId)) {
                  const label = suffix === 'Percentages' ? `${metric.Type} Percentage` : `${metric.Type} ${suffix}`;
                  columns.set(columnId, {
                    id: columnId,
                    label: label,
                    category: 'Expenses',
                    accessor: (row) => {
                      const metricData = row.expenses?.find(m => m.Type === metric.Type);
                      return metricData?.[prop] || '';
                    },
                  });
                }
              }
            });
          });
        }
      }
    });

    return Array.from(columns.values());
  }, [currentDeal, selectedPeriod]);

  const defaultColumns = useMemo(() => [
    'details.City, State',
    'details.State',
    'details.Total Beds',
    'details.Medicaid Rate',
    'occupancy.Total.ADC',
    'occupancy.Skilled.Percentages',
    'revenue.Adjusted Revenue.Annual',
    'revenue.Adjusted Revenue.PPD',
    'expenses.Adjusted Expenses.annual',
    'expenses.Adjusted Expenses.ppd',
    'expenses.Adjusted EBITDAR.ppd',
    'expenses.Adjusted EBITDAR.Annual',
    // Add HUD Debt columns to default selection if they exist
    'hudDebt.Original Loan',
    'hudDebt.Current Loan Balance',
    'hudDebt.Debt Service',
    'hudDebt.Interest Rate'
  ], []);

  useEffect(() => {
    const filteredDefaultColumns = defaultColumns.filter(
      id => availableColumns.find(col => col.id === id)
    );

    // Only set selected columns if they haven't been set yet or if the deal changes
    if (availableColumns.length > 0 && currentDeal && selectedColumns.length === 0) {
      setSelectedColumns(filteredDefaultColumns);
    }
  }, [availableColumns, currentDeal, defaultColumns, selectedColumns.length]);

  const handleResetColumns = () => {
    const filteredDefaultColumns = defaultColumns.filter(
      id => availableColumns.find(col => col.id === id)
    );
    setSelectedColumns(filteredDefaultColumns);
  };

  useEffect(() => {
    if (currentDeal && currentDeal.facilities) {
      let allFacilities = currentDeal.facilities;
      let formattedFacilityData = [];

      // Filter facilities based on selectedGroup if one is selected
      let filteredFacilities = allFacilities;
      if (selectedGroup && selectedGroup.facilityIds && selectedGroup.facilityIds.length > 0) {
        filteredFacilities = Object.entries(allFacilities)
          .filter(([key, facility]) =>
            selectedGroup.facilityIds.includes(key) ||
            selectedGroup.facilityIds.includes(facility.details?.Facilities)
          )
          .reduce((acc, [key, value]) => {
            acc[key] = value;
            return acc;
          }, {});
      }

      // Add consolidated data
      if (!selectedGroup && filteredFacilities.Consolidated) {
        // Only use the pre-existing Consolidated facility when no group is selected
        const consolidatedFacility = filteredFacilities.Consolidated;
        const periodData = consolidatedFacility.periods[selectedPeriod]?.Categories || {};

        // For non-group consolidated data, we still need to extract the Medicaid Rate
        let medicaidRate = '';
        if (consolidatedFacility.hudDebt?.Categories) {
          const medicaidData = Object.entries(consolidatedFacility.hudDebt.Categories)
            .find(([key]) => key.startsWith("Med")) || [];

          if (medicaidData.length > 0) {
            const [_, data] = medicaidData;
            const rateItem = data.find(item =>
              item.Type?.includes("Med") ||
              item.Type?.includes("Total Rate") ||
              item.Type?.includes("Current")
            );
            medicaidRate = rateItem?.Amount || '';
          }
        }

        // Extract HUD Debt data if it exists
        let hudDebtData = {};
        if (consolidatedFacility.hudDebt?.Categories?.["HUD Debt"]) {
          hudDebtData = {
            "HUD Debt": consolidatedFacility.hudDebt.Categories["HUD Debt"]
          };
        }

        const consolidatedData = {
          details: {
            ...consolidatedFacility.details,
            'Medicaid Rate': medicaidRate // Add extracted Medicaid Rate for non-group consolidated
          },
          occupancy: periodData.Occupancy || [],
          revenue: periodData.Revenue || [],
          expenses: periodData.Expenses || [],
          hudDebt: hudDebtData,
          isConsolidated: true
        };

        formattedFacilityData.push(consolidatedData);
      } else if (selectedGroup && selectedGroup.facilityIds && selectedGroup.facilityIds.length > 0) {
        // When a group is selected, use the pre-calculated data from DealContext
        if (currentFacility && currentFacility.details?.isGroup) {
          const periodData = currentFacility.periods[selectedPeriod]?.Categories || {};

          // Extract HUD Debt data if it exists for the group
          let hudDebtData = {};
          if (currentFacility.hudDebt?.Categories?.["HUD Debt"]) {
            hudDebtData = {
              "HUD Debt": currentFacility.hudDebt.Categories["HUD Debt"]
            };
          }

          // Simply use the data directly from DealContext, including the Medicaid Rate
          const groupConsolidatedData = {
            details: {
              ...currentFacility.details
              // No need to override Medicaid Rate - use what's already calculated in DealContext
            },
            occupancy: periodData.Occupancy || [],
            revenue: periodData.Revenue || [],
            expenses: periodData.Expenses || [],
            hudDebt: hudDebtData,
            isConsolidated: true
          };

          formattedFacilityData.push(groupConsolidatedData);
        }
      }

      // Then add all other filtered facilities
      const otherFacilities = Object.entries(filteredFacilities)
        .filter(([key]) => key !== 'Consolidated')
        .map(([facilityKey, facility]) => {
          const periodData = facility.periods[selectedPeriod]?.Categories || {};

          // For individual facilities, we need to extract the Medicaid Rate
          let medicaidRate = '';
          if (facility.hudDebt?.Categories) {
            const medicaidData = Object.entries(facility.hudDebt.Categories)
              .find(([key]) => key.startsWith("Med")) || [];

            if (medicaidData.length > 0) {
              const [_, data] = medicaidData;
              const rateItem = data.find(item =>
                item.Type?.includes("Med") ||
                item.Type?.includes("Total Rate") ||
                item.Type?.includes("Current")
              );
              medicaidRate = rateItem?.Amount || '';
            }
          }

          // Extract HUD Debt data if it exists
          let hudDebtData = {};
          if (facility.hudDebt?.Categories?.["HUD Debt"]) {
            hudDebtData = {
              "HUD Debt": facility.hudDebt.Categories["HUD Debt"]
            };
          }

          return {
            details: {
              ...facility.details,
              'Medicaid Rate': medicaidRate // Add extracted Medicaid Rate for individual facilities
            },
            occupancy: periodData.Occupancy || [],
            revenue: periodData.Revenue || [],
            expenses: periodData.Expenses || [],
            hudDebt: hudDebtData,
            isConsolidated: false
          };
        });

      formattedFacilityData = [...formattedFacilityData, ...otherFacilities];
      setTableData(formattedFacilityData);
    }
  }, [currentDeal, currentFacility, selectedPeriod, selectedGroup]);

  const columns = useMemo(() => {
    const facilitiesColumn = columnHelper.accessor(
      row => row.details?.Facilities,
      {
        id: 'details.Facilities',
        header: () => (
          <p className="text-sm font-bold text-gray-600 dark:text-white">FACILITY</p>
        ),
        cell: (info) => (
          <p className="text-sm font-bold text-navy-700 dark:text-white">{info.getValue()}</p>
        ),
      }
    );

    // Create a map of column order for consistent positioning
    const columnOrder = new Map();
    columnOrder.set('details.City, State', 0);
    columnOrder.set('details.State', 1);
    columnOrder.set('details.Total Beds', 2);
    columnOrder.set('details.Medicaid Rate', 3);
    columnOrder.set('occupancy.Total.ADC', 4);
    columnOrder.set('occupancy.Skilled.Percentages', 5);
    columnOrder.set('revenue.Adjusted Revenue.Annual', 6);
    columnOrder.set('revenue.Adjusted Revenue.PPD', 7);
    columnOrder.set('expenses.Adjusted Expenses.annual', 8);
    columnOrder.set('expenses.Adjusted Expenses.ppd', 9);
    columnOrder.set('expenses.Adjusted EBITDAR.Annual', 10);
    columnOrder.set('hudDebt.Original Loan', 11);
    columnOrder.set('hudDebt.Current Loan Balance', 12);
    columnOrder.set('hudDebt.Debt Service', 13);
    columnOrder.set('hudDebt.Interest Rate', 14);

    const selectedColumnsData = availableColumns
      .filter(col => selectedColumns.includes(col.id))
      .sort((a, b) => {
        const orderA = columnOrder.get(a.id) ?? 999;
        const orderB = columnOrder.get(b.id) ?? 999;
        return orderA - orderB;
      })
      .map(col => columnHelper.accessor(
        typeof col.accessor === 'function' ? col.accessor : (row) => {
          const value = getNestedValue(row, col.accessor);
          return value;
        },
        {
          id: col.id,
          header: () => (
            <p className="text-sm font-bold text-gray-600 dark:text-white">{col.label.toUpperCase()}</p>
          ),
          cell: (info) => {
            const value = info.getValue();

            // Use custom formatter if provided
            if (col.formatter && typeof col.formatter === 'function') {
              return (
                <p className="text-sm font-bold text-navy-700 dark:text-white">
                  {col.formatter(value)}
                </p>
              );
            }

            // Handling Facility Details category
            if (col.category === 'Facility Details') {
              if (col.label === 'Medicaid Rate' && value) {
                return (
                  <p className="text-sm font-bold text-navy-700 dark:text-white">
                    ${Number(value)}
                  </p>
                );
              }
              return (
                <p className="text-sm font-bold text-navy-700 dark:text-white">
                  {value}
                </p>
              );
            }

            // Handling HUD Debt category
            if (col.category === 'HUD Debt') {
              if (typeof value === 'number') {
                if (col.id.includes('Interest Rate')) {
                  return (
                    <p className="text-sm font-bold text-navy-700 dark:text-white">
                      {(value * 100).toFixed(2)}%
                    </p>
                  );
                }
                return (
                  <p className="text-sm font-bold text-navy-700 dark:text-white">
                    ${Math.round(value).toLocaleString()}
                  </p>
                );
              }
              return <p className="text-sm font-bold text-navy-700 dark:text-white">{value}</p>;
            }

            // Handling Occupancy category
            if (col.category === 'Occupancy') {
              if (typeof value === 'number') {
                if (col.id.includes('.ADC')) {
                  return (
                    <p className="text-sm font-bold text-navy-700 dark:text-white">
                      {Number.isInteger(value)
                        ? value.toLocaleString()
                        : value.toFixed(2)}
                    </p>
                  );
                }

                if (col.id.includes('.percentages') ||
                  (value <= 100 && value >= 0)) {
                  return (
                    <p className="text-sm font-bold text-navy-700 dark:text-white">
                      {value.toFixed(2)}%
                    </p>
                  );
                }

                return (
                  <p className="text-sm font-bold text-navy-700 dark:text-white">
                    {Number.isInteger(value)
                      ? value.toLocaleString()
                      : value.toFixed(2)}
                  </p>
                );
              }

              // Handle string values that already contain a percentage sign
              if (typeof value === 'string') {
                // If it already has a percentage sign, return as is
                if (value.includes('%')) {
                  return (
                    <p className="text-sm font-bold text-navy-700 dark:text-white">
                      {value}
                    </p>
                  );
                }
                // If it's a numeric string without percentage sign, add it
                const numValue = parseFloat(value);
                if (!isNaN(numValue) && numValue <= 100 && numValue >= 0) {
                  return (
                    <p className="text-sm font-bold text-navy-700 dark:text-white">
                      {numValue.toFixed(2)}%
                    </p>
                  );
                }
              }

              return <p className="text-sm font-bold text-navy-700 dark:text-white">{value}</p>;
            }

            // Existing logic for other categories
            if (typeof value === 'number') {
              // Handle PPD values
              if (col.id.toLowerCase().includes('ppd')) {
                return (
                  <p className="text-sm font-bold text-navy-700 dark:text-white">
                    ${Number(value).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })}
                  </p>
                );
              }

              // Handle percentage values
              if (col.id.toLowerCase().includes('percentages') ||
                (typeof value === 'number' && value <= 100 && value >= 0 &&
                  (col.category === 'Occupancy' || col.label.toLowerCase().includes('percent')))) {
                return (
                  <p className="text-sm font-bold text-navy-700 dark:text-white">
                    {Math.round(value)}%
                  </p>
                );
              }
              if (col.id.toLowerCase().includes('margin')) {
                return (
                  <p className="text-sm font-bold text-navy-700 dark:text-white">
                    {typeof value === 'number'
                      ? `${(value * 100).toFixed()}%`
                      : value}
                  </p>
                );
              }
              // Handle regular monetary values
              if (col.category?.includes('Revenue') || col.category?.includes('Expenses')) {
                return (
                  <p className="text-sm font-bold text-navy-700 dark:text-white">
                    ${Math.round(value).toLocaleString()}
                  </p>
                );
              }
              // Handle other numeric values
              return (
                <p className="text-sm font-bold text-navy-700 dark:text-white">
                  {Math.round(value).toLocaleString()}
                </p>
              );
            }
            return <p className="text-sm font-bold text-navy-700 dark:text-white">{value}</p>;
          },
        }
      ));

    return [facilitiesColumn, ...selectedColumnsData];
  }, [availableColumns, selectedColumns]);

  const handleColumnToggle = (columnIds) => {
    if (!Array.isArray(columnIds)) columnIds = [columnIds];

    setSelectedColumns(prev => {
      if (columnIds.length > 1 || columnIds.length === 0) {
        return columnIds;
      }

      const columnId = columnIds[0];
      if (prev.includes(columnId)) {
        return prev.filter(id => id !== columnId);
      } else {
        return [...prev, columnId];
      }
    });
  };

  const table = useReactTable({
    data: tableData,
    columns,
    state: {
      columnFilters,
      globalFilter,
    },
    onColumnFiltersChange: setColumnFilters,
    onGlobalFilterChange: setGlobalFilter,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  return (
    <Card extra={"w-full h-full p-4"}>
      <div className="flex w-full justify-between sm:p-4">
        <ColumnSelector
          selectedColumns={selectedColumns}
          onColumnToggle={handleColumnToggle}
          availableColumns={availableColumns}
          onResetColumns={handleResetColumns}
        />
        <Button
          onClick={() => {
            const periodSuffix = selectedPeriod ? `_${selectedPeriod.replace(/\s+/g, '_')}` : '';
            exportTableToCSV(tableData, columns, `${currentDealName || 'deal'}${periodSuffix}_data.csv`);
          }}
          size="large"
          className="flex items-center justify-center rounded-lg p-2 text-xl transition-opacity hover:cursor-pointer hover:opacity-80">
          <MdDownload />
        </Button>
      </div>
      <div className="max-h-[90vh] overflow-auto relative">
        <table className="w-full border-collapse">
          <thead>
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id} className="sticky top-0 z-30 !border-px !border-gray-400">
                {headerGroup.headers.map((header, index) => {
                  const isFrozenColumn = index === 0;
                  return (
                    <th
                      key={header.id}
                      colSpan={header.colSpan}
                      onClick={header.column.getToggleSortingHandler()}
                      className={`cursor-pointer border-b border-gray-200 pb-2 pr-4 pt-0 text-start dark:border-white/30 bg-white
                        ${isFrozenColumn ? 'sticky left-0 z-30 shadow-[2px_0_5px_-2px_rgba(0,0,0,0.1)] bg-gray-100 dark:bg-navy-900' : ''}`}
                    >
                      <div className="items-center justify-between text-xs text-gray-200">
                        {flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )}
                      </div>
                    </th>
                  );
                })}
              </tr>
            ))}
          </thead>
          <tbody>
            {table.getRowModel().rows.map((row) => {
              const isSelected = row.original.details?.Facilities === currentFacility?.details?.Facilities;
              const isConsolidated = row.original.isConsolidated;

              return (
                <tr
                  key={row.id}
                  className={`
                    ${isSelected ? "bg-red-100 dark:bg-blue-900" : ""}
                    ${isConsolidated ? "bg-blue-50 dark:bg-blue-900/50" : ""}
                  `}
                >
                  {row.getVisibleCells().map((cell, index) => {
                    const isFrozenColumn = index === 0;
                    return (
                      <td
                        key={cell.id}
                        className={`border-4 border-white py-4 px-4
                          ${isFrozenColumn ? `
                            sticky left-0 bg-white dark:bg-navy-800 shadow-[2px_0_5px_-2px_rgba(0,0,0,0.1)] z-20
                            ${isConsolidated ? '!bg-blue-100 dark:!bg-blue-800' : ''}
                            ${isSelected ? '!bg-red-100 dark:!bg-blue-900' : ''}
                          ` : ''}`}
                      >
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </Card>
  );
}

export default EnhancedTable;